/**
 * `demo/guide/autoplayPointer.ts` - the Autoplay pointer (#2824, #2962).
 *
 * While Autoplay plays, an arrow pointer shows on the page. Before each press
 * Autoplay makes, the pointer glides to the centre of the target (lead page or
 * a control inside the member-app phone frame), shows a press, and only then
 * the press fires. Presses run one at a time in a queue, so a visitor can
 * follow each one. Pause or Stop drops the queue and hides the pointer.
 *
 * With Autoplay off (the guide is not playing, or a test calls an action
 * directly) a press fires at once, as before.
 */

export type PointerMode = "off" | "playing" | "paused";

/**
 * The shortest glide, for a short move (#2962, founder 3 Nov 2026: the
 * pointer moved too quickly to follow). A viewer can follow a glide this long.
 */
export const GLIDE_MIN_MS = 900;
/**
 * The longest glide, for a move across the page. A 1400 px move then keeps a
 * mean speed of about 700 px/s (top speed about 850 px/s).
 */
export const GLIDE_MAX_MS = 2000;
/** Every glide takes at least this long per pixel, up to GLIDE_MAX_MS. */
export const GLIDE_MS_PER_PX = 2.5;
/** The pointer rests this long on its target before the press beat starts. */
export const ARRIVE_SETTLE_MS = 150;
/** The press beat: the pointer shows the press this long before it fires. */
export const PRESS_BEAT_MS = 300;
/** After a press, the next one waits this long so the screen can draw. */
export const SETTLE_MS = 450;
/**
 * Inside a step's bar a follow-on press waits only this long after the last
 * press (#2962): the press itself waits for its target to stop moving.
 */
export const IN_BAR_SETTLE_MS = 150;
/** A target that scrolls or slides into place gets at most this long to stop. */
const SCROLL_MAX_MS = 900;
/** A target that moved during the glide gets a short glide to its new place. */
const CORRECT_MS = 220;
/** A step's target still sliding in gets this long before the pointer stops waiting for it. */
const TARGET_WAIT_MS = 1500;
/** The pointer counts as resting on a target this near its centre. */
const REST_PX = 4;
/** The bar-time glide ends this long before the press beat starts. */
const FOLLOW_MARGIN_MS = 60;
/** The shortest bar-time glide for a move longer than a small correction. */
const FOLLOW_MIN_MS = 150;
/**
 * A follow-on press inside the bar (the confirm after Publish, the next
 * screen on the phone) glides at the same calm speed (#2962), at least this
 * long. When the follow-ons do not fit in the bar, the bar holds full in its
 * waiting mode until the action finishes.
 */
export const FOLLOW_ON_MIN_MS = 650;
const FOLLOW_ON_MAX_MS = 1600;
/**
 * The bar-time glide ends by this share of the bar (#2962), so the press
 * lands on arrival with time left for the press beat before the bar ends.
 */
export const ARRIVE_SHARE = 0.6;
/** A press target that does not show in this long: the pointer goes to the ring meanwhile. */
const FALLBACK_MS = 120;
/** The glide curve: a soft start and stop, at most about 1.2 times the mean speed. */
const EASE = "cubic-bezier(.3,.15,.7,.85)";

/**
 * When the step's bar ends (Date.now() time), or null when no bar runs
 * (#2962). The bar-time glide ends by ARRIVE_SHARE of the bar, so the press
 * lands on arrival before the bar ends.
 */
let barEndsAt: number | null = null;
/** When the step's bar started (Date.now() time). */
let barStartsAt: number | null = null;

/**
 * Autoplay sets this as each bar starts, and clears it when the bar stops.
 * `startedAt` is when the bar started (default: now).
 */
export function setBarEndsAt(at: number | null, startedAt: number = Date.now()): void {
  barEndsAt = at;
  barStartsAt = at === null ? null : Math.min(startedAt, at);
}

/** The bar's midpoint (Date.now() time), or null when no bar runs. */
export function barMidAt(): number | null {
  if (barEndsAt === null || barStartsAt === null) return null;
  return barStartsAt + (barEndsAt - barStartsAt) / 2;
}

/**
 * When the step's glide ends (Date.now() time), and whether it goes to the
 * step's press target (#2962). Autoplay presses on arrival. Null until the
 * step's pointer has a target.
 */
let arrival: { at: number; press: boolean } | null = null;

/** The current step's glide: when it ends, and whether it ends on the press target. */
export function pointerArrival(): { at: number; press: boolean } | null {
  return arrival;
}

type Job = {
  el: HTMLElement;
  act: () => void;
  resolve: (done: boolean) => void;
};

let mode: PointerMode = "off";
let generation = 0;
let queue: Job[] = [];
let running = false;
let busySince = 0;
let busyTotal = 0;
let lastDoneAt = 0;
let pos: { x: number; y: number } | null = null;
let node: HTMLDivElement | null = null;

function reducedMotion(): boolean {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  } catch {
    return false;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

const ARROW =
  '<svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true" focusable="false">' +
  '<path d="M5 3 L5 23 L10.2 18.3 L13.6 25.6 L17.2 24 L13.9 16.9 L21 16.6 Z" ' +
  'fill="#111" stroke="#fff" stroke-width="1.8" stroke-linejoin="round"/></svg>';

const RING_CSS = [
  "position:absolute",
  "left:-18px",
  "top:-18px",
  "width:36px",
  "height:36px",
  "border-radius:9999px",
  "border:2px solid #2563eb",
  "background:rgba(37,99,235,.18)",
  "opacity:0",
  "transform:scale(.3)",
  "pointer-events:none",
].join(";");

const ARROW_CSS = [
  "position:absolute",
  "left:-5px",
  "top:-3px",
  "display:block",
  "transform-origin:5px 3px",
  "transition:transform 120ms ease-out",
].join(";");

function ensureNode(): HTMLDivElement {
  if (node?.isConnected) return node;
  const el = document.createElement("div");
  el.setAttribute("data-testid", "demo-autoplay-pointer");
  el.setAttribute("aria-hidden", "true");
  el.style.cssText = [
    "position:fixed",
    "left:0",
    "top:0",
    "z-index:2147483000",
    "pointer-events:none",
    "width:28px",
    "height:28px",
    "will-change:transform",
    "filter:drop-shadow(0 2px 3px rgba(0,0,0,.35))",
    "display:none",
  ].join(";");
  const ring = document.createElement("span");
  ring.setAttribute("data-pointer-ripple", "");
  ring.style.cssText = RING_CSS;
  const arrow = document.createElement("span");
  arrow.setAttribute("data-pointer-arrow", "");
  arrow.style.cssText = ARROW_CSS;
  arrow.innerHTML = ARROW;
  el.append(ring, arrow);
  document.body.appendChild(el);
  node = el;
  return el;
}

function place(x: number, y: number, ms: number): void {
  const el = ensureNode();
  if (ms > 0) {
    // Fix the start place first: a pointer that was hidden or placed in this
    // same task would jump instead of glide (#2962).
    void el.offsetWidth;
  }
  el.style.transition = ms > 0 ? `transform ${ms}ms ${EASE}` : "none";
  el.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  el.setAttribute("data-x", String(Math.round(x)));
  el.setAttribute("data-y", String(Math.round(y)));
  pos = { x, y };
  glideEndsAt = ms > 0 ? Date.now() + ms : 0;
}

/** When the last glide ends (Date.now() time), or 0 after a jump. */
let glideEndsAt = 0;

/** The pointer rests on its target a moment before the press beat starts (#2962). */
async function settleBeforePress(): Promise<void> {
  if (reducedMotion() || glideEndsAt === 0) return;
  const rest = glideEndsAt + ARRIVE_SETTLE_MS - Date.now();
  if (rest > 0) await sleep(Math.min(rest, ARRIVE_SETTLE_MS));
}

function show(on: boolean): void {
  if (!on && !node) return;
  const el = ensureNode();
  el.style.display = on ? "block" : "none";
  if (on) el.style.opacity = "1";
  el.setAttribute("data-visible", on ? "true" : "false");
}

/** Where the pointer starts: the guide card, else low in the middle of the page. */
function startPoint(): { x: number; y: number } {
  const card = document.querySelector<HTMLElement>('[data-testid="demo-guide"]');
  const r = card?.getBoundingClientRect();
  if (r && r.width > 0) return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  return { x: window.innerWidth / 2, y: window.innerHeight * 0.7 };
}

/** A place inside the window (the window may have changed size since). */
function onScreen(at: { x: number; y: number }): boolean {
  return at.x >= 0 && at.y >= 0 && at.x <= window.innerWidth && at.y <= window.innerHeight;
}

/** The centre of an element in the parent page, across the member-app frame. */
export function centreOnPage(el: HTMLElement): { x: number; y: number } {
  const r = el.getBoundingClientRect();
  let x = r.left + r.width / 2;
  let y = r.top + r.height / 2;
  const doc = el.ownerDocument;
  if (doc === document) {
    // An element larger than the window (the roster grid): the centre of the
    // part on screen, where the ring is drawn (#2962).
    const left = Math.max(0, r.left);
    const right = Math.min(window.innerWidth, r.right);
    const top = Math.max(0, r.top);
    const bottom = Math.min(window.innerHeight, r.bottom);
    if (right > left && bottom > top) {
      x = (left + right) / 2;
      y = (top + bottom) / 2;
    }
  }
  if (doc !== document) {
    const frame = Array.from(document.querySelectorAll("iframe")).find(
      (f) => f.contentDocument === doc,
    );
    if (frame) {
      const fr = frame.getBoundingClientRect();
      // The frame is drawn scaled: its rect over its own layout width.
      const scale = frame.offsetWidth > 0 ? fr.width / frame.offsetWidth : 1;
      x = fr.left + (frame.clientLeft + x) * scale;
      y = fr.top + (frame.clientTop + y) * scale;
    }
  }
  return { x, y };
}

/** How near the guide card may come to the target's centre before it steps aside. */
const COVER_MARGIN_PX = 16;

/**
 * The guide card can sit over the target (the phone's lower bar on a short
 * window): the pointer would seem to press the card. The card fades while
 * the pointer presses, and comes back after. Returns the undo.
 */
export function uncover(at: { x: number; y: number }): () => void {
  const card = document.querySelector<HTMLElement>('[data-testid="demo-guide-card"]');
  if (!card) return () => {};
  const r = card.getBoundingClientRect();
  const covered =
    at.x >= r.left - COVER_MARGIN_PX &&
    at.x <= r.right + COVER_MARGIN_PX &&
    at.y >= r.top - COVER_MARGIN_PX &&
    at.y <= r.bottom + COVER_MARGIN_PX;
  if (!covered) return () => {};
  const { opacity, transition, pointerEvents } = card.style;
  card.style.transition = "opacity 200ms ease-out";
  card.style.opacity = "0.08";
  card.style.pointerEvents = "none";
  card.setAttribute("data-pointer-cleared", "true");
  return () => {
    card.style.opacity = opacity;
    card.style.transition = transition;
    card.style.pointerEvents = pointerEvents;
    card.removeAttribute("data-pointer-cleared");
  };
}

/** True when the element has a box on screen (a closed menu item has none). */
function hasBox(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 || r.height > 0;
}

/**
 * How long a glide takes when no bar limits it (#2962): GLIDE_MS_PER_PX, at
 * least GLIDE_MIN_MS and at most GLIDE_MAX_MS.
 */
export function calmGlideMs(dist: number): number {
  return Math.round(Math.min(GLIDE_MAX_MS, Math.max(GLIDE_MIN_MS, dist * GLIDE_MS_PER_PX)));
}

function glideMs(from: { x: number; y: number }, to: { x: number; y: number }): number {
  return calmGlideMs(Math.hypot(to.x - from.x, to.y - from.y));
}

/** The glide of a follow-on press inside the bar (#2962), at the same calm speed. */
export function followOnMs(dist: number): number {
  return Math.round(Math.min(FOLLOW_ON_MAX_MS, Math.max(FOLLOW_ON_MIN_MS, dist * GLIDE_MS_PER_PX)));
}

/**
 * Bring the target into view in every scroll box it sits in (the page, a
 * panel, the phone's own screen). "nearest" does not move a target that shows.
 */
function scrollIntoView(el: HTMLElement, instant = false): void {
  if (typeof el.scrollIntoView !== "function") return;
  el.scrollIntoView({
    block: "nearest",
    inline: "nearest",
    behavior: instant || reducedMotion() ? "instant" : "smooth",
  });
}

/**
 * True when a control inside the phone frame shows on the phone's screen
 * (#2962): its top and bottom edges hit the control itself, not the tab bar
 * over it and not nothing (below the frame's viewport). True for any element
 * of the page itself.
 */
export function showsInFrame(el: HTMLElement): boolean {
  const doc = el.ownerDocument;
  if (doc === document || typeof doc.elementFromPoint !== "function") return true;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return false;
  const x = r.left + r.width / 2;
  const inset = Math.min(2, r.height / 2);
  return [r.top + inset, r.bottom - inset].every((y) => {
    const hit = doc.elementFromPoint(x, y);
    return hit !== null && (hit === el || el.contains(hit));
  });
}

/**
 * Bring the target into view and wait for it to stop. A control inside the
 * phone that still does not show (a smooth scroll that never ran: the frame
 * draws slowly, or the page is hidden) jumps into view (#2962). The phone's
 * scroll padding keeps it above the tab bar.
 */
async function bringIntoView(el: HTMLElement): Promise<void> {
  scrollIntoView(el);
  await settled(el);
  if (!el.isConnected || showsInFrame(el)) return;
  scrollIntoView(el, true);
  await settled(el);
}

/** Wait until the target stops moving (a scroll, a sliding panel), up to SCROLL_MAX_MS. */
async function settled(el: HTMLElement): Promise<void> {
  let at = centreOnPage(el);
  for (let waited = 0; waited < SCROLL_MAX_MS; waited += 60) {
    await sleep(60);
    const now = centreOnPage(el);
    if (Math.abs(now.x - at.x) < 1 && Math.abs(now.y - at.y) < 1) return;
    at = now;
  }
}

function ripple(): void {
  const el = ensureNode();
  const ring = el.querySelector<HTMLElement>("[data-pointer-ripple]");
  const arrow = el.querySelector<HTMLElement>("[data-pointer-arrow]");
  if (arrow) arrow.style.transform = "scale(.82)";
  window.setTimeout(() => {
    if (arrow) arrow.style.transform = "scale(1)";
  }, PRESS_BEAT_MS * 0.6);
  if (ring && typeof ring.animate === "function") {
    ring.animate(
      [
        { opacity: 0.9, transform: "scale(.3)" },
        { opacity: 0, transform: "scale(1.4)" },
      ],
      { duration: PRESS_BEAT_MS + 250, easing: "ease-out" },
    );
  }
  el.setAttribute("data-pressing", "true");
}

/** The pointer rests on this place already (it went there while the step's bar filled). */
function restsOn(at: { x: number; y: number }): boolean {
  return pos !== null && Math.hypot(pos.x - at.x, pos.y - at.y) <= REST_PX;
}

async function runJob(job: Job, gen: number): Promise<boolean> {
  const alive = () => gen === generation && mode === "playing";
  if (!job.el.isConnected || !alive()) return false;
  // The step's glide is still on its way (#2962): the press waits for the
  // pointer to arrive, so the press beat never starts in mid-air.
  const inFlight = arrival ? arrival.at - Date.now() : 0;
  if (inFlight > 0) await sleep(inFlight);
  if (!job.el.isConnected || !alive()) return false;
  // The pointer went to this target while the step's bar filled (#2962): it
  // rests on it or near it, so only the press beat is left and the press
  // lands on arrival.
  const nearNow =
    pos !== null &&
    Math.hypot(pos.x - centreOnPage(job.el).x, pos.y - centreOnPage(job.el).y) <= 40;
  const ready = hasBox(job.el) && onScreen(centreOnPage(job.el)) && nearNow && showsInFrame(job.el);
  // A target with no box (a closed menu's item) has nothing to scroll or wait for.
  if (!ready && hasBox(job.el)) await bringIntoView(job.el);
  if (!job.el.isConnected || !alive()) return false;
  // A target with no box (a menu item still closed), or one that is not on
  // screen (outside the phone's visible screen): press it where the pointer
  // is. The pointer never flies to the page's corner or off screen (#2962).
  const to = centreOnPage(job.el);
  if (!hasBox(job.el) || !onScreen(to)) {
    // The press beat, with the pointer left where it is.
    if (!reducedMotion()) await sleep(PRESS_BEAT_MS);
    if (!job.el.isConnected || !alive()) return false;
    job.act();
    return true;
  }
  // The step's glide just arrived: the pointer rests a moment first.
  if (restsOn(to)) await settleBeforePress();
  if (!job.el.isConnected || !alive()) return false;
  const from = pos && onScreen(pos) ? pos : startPoint();
  if (from !== pos) place(from.x, from.y, 0);
  show(true);
  const dist = Math.hypot(to.x - from.x, to.y - from.y);
  const near = dist <= 40;
  // A follow-on press inside the bar gets a short glide (#2962).
  const inBar = barEndsAt !== null && barEndsAt > Date.now();
  const ms =
    reducedMotion() || restsOn(to)
      ? 0
      : near
        ? CORRECT_MS
        : inBar
          ? followOnMs(dist)
          : glideMs(from, to);
  ensureNode().setAttribute("data-pressing", "false");
  let restore = uncover(to);
  try {
    place(to.x, to.y, ms);
    if (ms > 0) await sleep(ms);
    if (!job.el.isConnected || !alive()) return false;
    // The target moved while the pointer glided: follow it the last bit.
    // A target that left the screen meanwhile keeps the pointer where it is.
    const now = centreOnPage(job.el);
    if (Math.hypot(now.x - to.x, now.y - to.y) > 4 && onScreen(now)) {
      restore();
      restore = uncover(now);
      place(now.x, now.y, ms > 0 ? CORRECT_MS : 0);
      if (ms > 0) await sleep(CORRECT_MS);
      if (!alive()) return false;
    }
    if (ms > 0) await settleBeforePress();
    if (!job.el.isConnected || !alive()) return false;
    ripple();
    await sleep(PRESS_BEAT_MS);
    ensureNode().setAttribute("data-pressing", "false");
    if (!job.el.isConnected || !alive()) return false;
    job.act();
    return true;
  } finally {
    restore();
  }
}

async function drain(): Promise<void> {
  if (running) return;
  running = true;
  busySince = Date.now();
  try {
    while (queue.length > 0) {
      const job = queue.shift() as Job;
      const gen = generation;
      let done = false;
      try {
        done = await runJob(job, gen);
      } catch {
        done = false;
      }
      lastDoneAt = Date.now();
      job.resolve(done);
    }
  } finally {
    busyTotal += Date.now() - busySince;
    running = false;
  }
}

/**
 * Do `act` on `el` as Autoplay. While Autoplay plays, the pointer first
 * glides to `el` and shows a press; the promise settles after `act` ran
 * (true) or was dropped (false). Otherwise `act` runs at once.
 */
export function pointAndAct(el: HTMLElement, act: () => void): Promise<boolean> {
  if (mode === "off") {
    act();
    return Promise.resolve(true);
  }
  if (mode === "paused") return Promise.resolve(false);
  // The presses move the pointer from here on (#2962): the step's follow
  // stops, so the pointer never glides back to the step's first target
  // between two presses.
  followGen += 1;
  return new Promise<boolean>((resolve) => {
    queue.push({ el, act, resolve });
    void drain();
  });
}

let followGen = 0;

/** Where the pointer is drawn now (mid-glide too), or its last place. */
function drawnAt(): { x: number; y: number } | null {
  if (!node?.isConnected || node.style.display === "none") return pos;
  const r = node.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return pos;
  return { x: r.left, y: r.top };
}

/**
 * How long a glide while the bar fills takes (#2962): a calm speed a viewer
 * can follow (calmGlideMs), and it ends by ARRIVE_SHARE of the bar, so the
 * press lands on arrival before the bar ends. A target that shows after that
 * point still arrives before the press beat would pass the bar's end.
 */
export function followMs(
  dist: number,
  now = Date.now(),
  endsAt = barEndsAt,
  startsAt: number | null = endsAt === barEndsAt ? barStartsAt : null,
): number {
  if (dist <= REST_PX) return 0;
  if (dist <= 40) return CORRECT_MS;
  let ms = calmGlideMs(dist);
  // A bar that has ended already does not count (a ring that moved after the press).
  if (endsAt !== null && endsAt > now) {
    const end = endsAt - PRESS_BEAT_MS - FOLLOW_MARGIN_MS;
    const start = startsAt ?? now;
    // It ends by ARRIVE_SHARE of the bar, so the click lands before the bar ends.
    const arriveBy = start + (endsAt - start) * ARRIVE_SHARE;
    ms = Math.min(ms, (arriveBy > now + FOLLOW_MIN_MS ? Math.min(arriveBy, end) : end) - now);
  }
  return Math.round(Math.max(FOLLOW_MIN_MS, ms));
}

/** True when the element can take the pointer now: a box, on screen, shown on the phone's screen. */
function canAim(el: HTMLElement | null | undefined): boolean {
  return Boolean(el?.isConnected && hasBox(el) && onScreen(centreOnPage(el)) && showsInFrame(el));
}

export type FollowOptions = {
  /**
   * The control the step's press will hit (#2962). Read every frame from
   * the landing frame on, until it shows: the pointer glides to it the frame
   * it shows. Until then the pointer goes to the lit element.
   */
  press?: (() => HTMLElement | null | undefined) | undefined;
};

/**
 * Autoplay's pointer follows the step (#2962): from the frame the step lands
 * it glides to the control the step's press will hit (else to the lit
 * element), so it arrives while the bar fills and the press lands as the bar
 * ends. It hides (opacity 0) when the step lights nothing. A press in the
 * queue moves the pointer itself, so this leaves it alone while one runs.
 * `still` is kept for callers: the target is read every frame either way.
 */
export function followStep(
  el: HTMLElement | null,
  _still = false,
  options: FollowOptions = {},
): void {
  const token = ++followGen;
  arrival = null;
  if (mode !== "playing") return;
  if (!el) {
    if (node) node.style.opacity = "0";
    return;
  }
  const started = Date.now();
  let aimed: HTMLElement | null = null;
  let aimedAt: { x: number; y: number } | null = null;
  let scrolled: HTMLElement | null = null;
  const live = () => token === followGen && mode === "playing";
  // One frame: find the target, and glide when it is new or has moved.
  const frame = (): boolean => {
    if (!live()) return false;
    if (running || queue.length > 0) return true;
    let target: HTMLElement | null = null;
    const press = options.press?.() ?? null;
    if (press && canAim(press)) {
      target = press;
    } else if (press?.isConnected && hasBox(press) && press.ownerDocument !== document) {
      // A phone control below the phone's screen: bring it up, then aim.
      if (scrolled !== press) {
        scrolled = press;
        scrollIntoView(press);
      }
    }
    if (!target && (!options.press || Date.now() - started >= FALLBACK_MS)) {
      if (canAim(el)) target = el;
    }
    if (!target) return true;
    const to = centreOnPage(target);
    if (aimed === target && aimedAt && Math.hypot(to.x - aimedAt.x, to.y - aimedAt.y) <= REST_PX)
      return true;
    const from = drawnAt() ?? startPoint();
    if (!pos || !onScreen(pos)) place(from.x, from.y, 0);
    show(true);
    ensureNode().setAttribute("data-pressing", "false");
    const dist = Math.hypot(to.x - from.x, to.y - from.y);
    const ms = reducedMotion() ? 0 : followMs(dist);
    place(to.x, to.y, ms);
    arrival = { at: Date.now() + ms, press: target === press };
    aimed = target;
    aimedAt = to;
    return true;
  };
  // The pointer shows in the frame the step lands, where it is.
  if (!running && queue.length === 0) {
    const at = pos && onScreen(pos) ? pos : startPoint();
    if (at !== pos) place(at.x, at.y, 0);
    show(true);
  }
  // The landing frame: glide now when the target is there.
  frame();
  void (async () => {
    const until = () => Math.max(started + TARGET_WAIT_MS, barEndsAt ?? 0);
    while (live() && el.isConnected && Date.now() < until()) {
      await sleep(16);
      if (!frame()) return;
    }
  })();
}

/** True while a press waits in the queue or the pointer is still moving. */
export function pointerBusy(): boolean {
  return running || queue.length > 0;
}

/** True while presses must wait: the pointer moves, or the last press is still drawing. */
export function pointerHolds(): boolean {
  if (mode === "paused") return true;
  if (mode === "off") return false;
  const inBar = barEndsAt !== null && barEndsAt > Date.now();
  return pointerBusy() || Date.now() - lastDoneAt < (inBar ? IN_BAR_SETTLE_MS : SETTLE_MS);
}

/** The time the pointer spent moving, all told (to stretch give-up timers). */
export function pointerBusyMs(): number {
  return busyTotal + (running ? Date.now() - busySince : 0);
}

/** Changes with every Play, Pause and Stop: a wait from before stops. */
export function pointerGeneration(): number {
  return generation;
}

export function getPointerMode(): PointerMode {
  return mode;
}

/** Autoplay's mode: the pointer shows while it plays and hides otherwise. */
export function setPointerMode(next: PointerMode): void {
  if (next === mode) return;
  mode = next;
  generation += 1;
  followGen += 1;
  const dropped = queue;
  queue = [];
  for (const job of dropped) job.resolve(false);
  if (next === "playing") {
    if (!pos || !onScreen(pos)) {
      const at = startPoint();
      place(at.x, at.y, 0);
    }
    show(true);
    return;
  }
  show(false);
  if (next === "off") pos = null;
}

/** Tests only: forget the pointer. */
export function resetPointerForTests(): void {
  setPointerMode("off");
  queue = [];
  running = false;
  busyTotal = 0;
  lastDoneAt = 0;
  pos = null;
  arrival = null;
  glideEndsAt = 0;
  barEndsAt = null;
  barStartsAt = null;
  node?.remove();
  node = null;
}
